import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  StorageFailure, StorageOp, clearStorageFailureSink, purgeCeilingProbeKey, runWithoutFailureSink,
  setStorageFailureSink,
} from 'src/app/shared/utils/safe-storage';
import {
  QuotaDiag, appendQuotaDiag, buildQuotaDiag, getLastPersistAction,
} from 'src/app/shared/utils/storage-quota-diag';
import { getTabId } from './tab-id';

/**
 * Key prefix for diagnostics parked in the user-drafts table. Chosen so the draft pickers never see
 * them: they list by feature prefix (`si:` / `tr:` / `un:`), so a `diag:` key is invisible in the UI
 * while remaining readable in the database.
 */
export const QUOTA_DIAG_KEY_PREFIX = 'diag:qx:';

/** `user_draft.type` is VARCHAR(50); a key past that is a 500, not a truncation. */
const MAX_DRAFT_KEY_LENGTH = 50;

/**
 * Most reports to send per page load.
 *
 * The metareducer writes on EVERY non-router action, so a storage area that has started refusing
 * refuses again on every click. The first report already carries the cause; a second and third add
 * the growth between them, and anything beyond that is the same failure re-posted forever.
 */
const MAX_REPORTS_PER_PAGE = 3;

/** Minimum gap between reports, so a burst of dispatches cannot turn into a burst of requests. */
const MIN_REPORT_INTERVAL_MS = 60000;

/**
 * Ships a diagnostic when browser storage refuses a write (see `storage-quota-diag`).
 *
 * WHY THIS EXISTS: the failure is a production-only one that nobody has reproduced on demand -- it
 * needs a specific user, on a specific browser, with a specific amount of state. `safe-storage`
 * stops it from breaking the session, which also removed the only signal that it happened at all.
 * This service turns each occurrence into a record that can be read later without the user having to
 * do anything: they click, it fails silently as before, and the evidence is waiting in the database.
 *
 * DELIVERY: `POST /v2/userDrafts/{key}` -- an existing authenticated endpoint that stores an
 * arbitrary JSON blob against the calling user, chosen over a new logging endpoint because it needs
 * no backend change. Consequences of that choice, all deliberate:
 *
 * - `keep=false` is REQUIRED. The endpoint defaults to `keep=true`, which archives the previous
 *   value into a new timestamped row on every save; a probe that fires on every dispatch would turn
 *   that into unbounded row growth. With `keep=false` each tab overwrites its own single row.
 * - The key carries the tab id, so two tabs of the app do not overwrite each other's evidence.
 * - `listDrafts` filters by the CALLING user's id, so these are not readable through the API by
 *   anyone but the affected user. Retrieval is a query against `user_draft` where `type` starts
 *   with `diag:`.
 * - The request is carved out of the HTTP interceptor's 401 handling (see `SharedHttpInterceptor`).
 *   Without that carve-out a report sent with an expired session would trigger the refresh-then-
 *   teardown path and log the user out -- a diagnostic must never do that.
 */
@Injectable({ providedIn: 'root' })
export class QuotaDiagReporterService implements OnDestroy {

  private reportsSent = 0;
  private lastReportAt = 0;
  /** Ops already reported once. A first failure of a KIND is never rate-limited away -- see below. */
  private readonly reportedOps = new Set<StorageOp>();
  private readonly sink = (failure: StorageFailure) => this.onStorageFailure(failure);

  constructor(private http: HttpClient) {
    // EVERYTHING THIS SERVICE DOES TO STORAGE ON ITS OWN BEHALF HAPPENS BEFORE THE SINK IS LIVE.
    //
    // Mint the tab id now, at boot, rather than on the first failure: minting reads and writes
    // sessionStorage, and those accesses fail in exactly the mode this service exists to report.
    // Registering the sink first would let them report THEMSELVES: they would consume the
    // first-of-op waiver for both `read` and `write` (see shouldReport) and two of the three
    // reports this page is allowed, so the metareducer's hydrate read and first persist write --
    // the failures carrying the payload size and the slice breakdown -- would be rate-limited
    // behind two near-empty reports (no `source`, so no slices, and `action: null`).
    getTabId();
    // A previous page may have left the ceiling probe's filler behind (its cleanup can be refused
    // too). Nothing else ever writes that key, so if this does not drop it, it stays forever --
    // in the quota the probe exists to measure. (Self-guarded, never reports.)
    purgeCeilingProbeKey('local');
    purgeCeilingProbeKey('session');
    setStorageFailureSink(this.sink);
  }

  /** Only clears the sink if it is still ours, so a newer consumer is not silently switched off. */
  ngOnDestroy(): void {
    clearStorageFailureSink(this.sink);
  }

  /**
   * Handle one refused access. Never throws: `safe-storage` calls this from the failure path of a
   * write that may itself be running inside the NgRx dispatch chain.
   *
   * Everything past the rate-limit decision is DEFERRED off that chain. Building the record walks
   * the payload slice by slice (a `JSON.stringify` each), probes the write ceiling and posts -- all
   * of it synchronous, on a blob big enough to have been refused, at the exact moment the user
   * clicked. The user already lost the write; they should not also lose the frame. What cannot wait
   * is the stamped action (later dispatches overwrite it) and the budget decision (a burst of
   * failures would otherwise all pass the check before the first deferred one runs).
   */
  private onStorageFailure(failure: StorageFailure): void {
    try {
      if (!this.shouldReport(failure.op)) {
        return;
      }
      const action = getLastPersistAction(failure.key);
      setTimeout(() => this.report(failure, action), 0);
    } catch {
      // A probe that breaks the app it observes is worse than no probe.
    }
  }

  /**
   * Build, keep a local copy, and ship. Runs on its own task -- never inside a reducer.
   *
   * Wrapped in `runWithoutFailureSink` because it left the window `safe-storage` holds open while a
   * sink runs inline: the local ring is written into the very area that just refused, so without
   * this its own refusal would come straight back through the sink and report itself.
   */
  private report(failure: StorageFailure, action: string | null): void {
    runWithoutFailureSink(() => {
      try {
        const diag = buildQuotaDiag(failure, action);
        // Local copy first: it survives an offline or rejected request, and `copy(__quotaDiag())`
        // pulls it off the user's machine when the database copy never arrived.
        appendQuotaDiag(diag);
        this.send(diag);
      } catch {
        // A probe that breaks the app it observes is worse than no probe.
      }
    });
  }

  /**
   * Rate limit: a refusing storage area fails on every dispatch, and each report is a request.
   *
   * The interval is waived for the first failure of each OP, and that exception is the point: when
   * the area is refusing outright, the boot-time hydrate READ fails first and carries no payload at
   * all (`chars: null`). Charging the interval against it would silence the WRITE failure -- the one
   * carrying the size and the slice breakdown -- for a full minute, which is most of the time the
   * user spends on the page. The total budget still caps the traffic.
   */
  private shouldReport(op: StorageOp): boolean {
    const now = Date.now();
    if (this.reportsSent >= MAX_REPORTS_PER_PAGE) {
      return false;
    }
    const firstOfItsKind = !this.reportedOps.has(op);
    if (!firstOfItsKind && this.reportsSent > 0 && now - this.lastReportAt < MIN_REPORT_INTERVAL_MS) {
      return false;
    }
    this.reportedOps.add(op);
    this.reportsSent++;
    this.lastReportAt = now;
    return true;
  }

  /**
   * Fire and forget. Every outcome is ignored on purpose -- a 401 (session gone), a 500, or no
   * network are all states in which there is nothing useful left to do, and the local ring already
   * holds the same record.
   */
  private send(diag: QuotaDiag): void {
    const url = `${environment.apiUrl}/v2/userDrafts/${this.draftKey()}?keep=false`;
    this.http.post(url, diag).subscribe({ next: () => undefined, error: () => undefined });
  }

  /**
   * `diag:qx:<tabId>`, clamped to the column width.
   *
   * Via `getTabId`, not a bare storage read: when sessionStorage is refusing writes the id lives
   * only in memory, and a storage read would hand back nothing -- so every tab would key its
   * evidence identically and, with `keep=false`, overwrite the others' in exactly the failure mode
   * this exists to investigate.
   */
  private draftKey(): string {
    const tabId = getTabId();
    return `${QUOTA_DIAG_KEY_PREFIX}${tabId}`.slice(0, MAX_DRAFT_KEY_LENGTH);
  }
}
